import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingAlongService } from '../services/SingAlongService';
import { useSingAlongBasePath } from '../hooks/useSingAlongBasePath';

const C = {
  ink: '#1A1033',
  ink2: '#241646',
  grape: '#8B82F0',
  grapeDeep: '#6B5FD9',
  punch: '#FF5A3C',
  mint: '#22C28A',
  sun: '#FFD23F',
  cloud: '#FAF7FF',
  cloud2: '#F0EBFF',
};

const SCORES = [94, 88, 91, 97, 85, 99, 90];

const heroBg = {
  backgroundColor: C.ink,
  backgroundImage: [
    'radial-gradient(closest-side at 50% 8%, rgba(255,210,63,.18), transparent 70%)',
    'radial-gradient(1100px 560px at 50% -12%, rgba(139,130,240,.38), transparent 62%)',
    'radial-gradient(820px 520px at 88% 12%, rgba(255,90,60,.14), transparent 60%)',
  ].join(','),
};

const logoGradient = 'linear-gradient(140deg, #FF5A3C, #FFD23F)';

const reactions = ['🎤', '🔥', '👏', '🏆', '😱'];

const experienceCards = [
  {
    icon: '⚖️',
    iconBg: 'rgba(255,90,60,.14)',
    title: 'An AI judge, not a cheerleader',
    text: 'Real scoring on pitch and timing — not the grandma-says-you-were-great kind. The number is honest, and that is exactly why it stings.',
  },
  {
    icon: '📊',
    iconBg: 'rgba(139,130,240,.18)',
    title: 'Your Vocal Blueprint',
    text: 'Pitch, timing, range and power, broken down after every take. Shareable proof of who actually carried the chorus.',
  },
  {
    icon: '👑',
    iconBg: 'rgba(255,210,63,.22)',
    title: 'Crown a champion',
    text: 'Brackets, comebacks and a live leaderboard. Someone leaves the night a legend, the rest leave plotting a rematch.',
  },
];

const steps = [
  { n: '1', title: 'Queue the songs', text: 'The host picks the setlist or lets the room shout out requests. Pop, classics, holiday chaos — your call.' },
  { n: '2', title: 'Sing & get scored', text: 'Grab the mic, give it everything. The AI judge scores the attempt the second the last note lands.' },
  { n: '3', title: 'Crown the winner', text: 'The leaderboard updates live. Bragging rights, rematches and one very smug champion.' },
];

const generations = [
  { tag: 'the headliner', title: "Kids' birthdays", gradient: 'linear-gradient(150deg, #FF7A4D, #FF5A3C)' },
  { tag: 'main character', title: 'Gen Z & teens', gradient: 'linear-gradient(150deg, #8B82F0, #6B5FD9)' },
  { tag: 'throwback', title: 'Millennial nights', gradient: 'linear-gradient(150deg, #22C28A, #149e6e)' },
  { tag: 'crowd favorite', title: 'Boomers & family', gradient: 'linear-gradient(150deg, #3a2a6e, #241646)' },
];

const checklist = [
  'Auto-saved clips of every performance',
  'Scores baked right onto the video',
  'Vertical, ready for Reels & TikTok',
  'Tag the squad, post the W',
];

const stats = [
  { num: '60s', label: 'From "let\u2019s do this" to first song' },
  { num: '8\u201398', label: 'The age range that shows up to sing' },
  { num: '0', label: 'Apps your guests have to download' },
];

const tiers = [
  { name: '24-Hour', price: '$9.99', features: ['1 song bundle', '24 hours access', 'Unlimited participants', 'Live leaderboard', 'AI scoring'] },
  { name: '3-Day', price: '$19.99', tag: 'Popular', features: ['More party time', '3 days access', 'Unlimited participants', 'Live leaderboard', 'AI scoring'] },
  { name: '7-Day', price: '$29.99', tag: 'Best value', features: ['Full week access', '7 days access', 'Unlimited participants', 'Live leaderboard', 'AI scoring'] },
  { name: '1-Month', price: '$49.99', features: ['Full catalog', '30 days access', 'Unlimited participants', 'Live leaderboard', 'AI scoring'] },
];

export const CanYouSingLanding: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSingAlongBasePath();
  const [scoreIdx, setScoreIdx] = useState(0);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [accountDest, setAccountDest] = useState(`/${basePath}/dashboard`);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setScoreIdx((i) => (i + 1) % SCORES.length), 1500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (user?.email_confirmed_at) {
        setUserEmail(user.email || '');
        try {
          setAccountDest(await SingAlongService.getAccountDestination(basePath));
        } catch {
          setAccountDest(`/${basePath}/dashboard`);
        }
      }
    })();
  }, [basePath]);

  const goSignup = () => navigate(`/${basePath}/signup`);
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await SingAlongService.signOut();
      setUserEmail(null);
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div style={{ background: C.cloud, color: C.ink, fontFamily: 'Inter, system-ui, sans-serif' }} className="min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes cysFloat { 0% { transform: translateY(20px); opacity: 0 } 15% { opacity: .9 } 100% { transform: translateY(-220px); opacity: 0 } }
        @keyframes cysMarquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }
        @keyframes cysPulse { 0%,100% { box-shadow: 0 14px 38px -8px rgba(255,90,60,.55) } 50% { box-shadow: 0 14px 50px -4px rgba(255,90,60,.85) } }
        .cys-pulse { animation: cysPulse 2.4s ease-in-out infinite; }
        .cys-float { animation: cysFloat 7s ease-in forwards; }
        .cys-marquee { animation: cysMarquee 28s linear infinite; }
        .cys-card { transition: transform .25s ease, box-shadow .25s ease; }
        .cys-card:hover { transform: translateY(-6px); }
        @media (prefers-reduced-motion: reduce) {
          .cys-pulse, .cys-float, .cys-marquee { animation: none !important; }
        }
      `}</style>

      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="mx-auto flex items-center justify-between py-6" style={{ width: '92vw', maxWidth: 1120 }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shadow-lg" style={{ background: logoGradient, boxShadow: '0 10px 24px -6px rgba(255,90,60,.45)' }}>
              🎤
            </div>
            <div className="leading-tight">
              <div className="font-extrabold text-white text-lg">Game Night</div>
              <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'rgba(255,255,255,.55)' }}>The Singing League</div>
            </div>
          </div>
          <button
            onClick={goSignup}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition"
            style={{ background: 'rgba(255,255,255,.1)', backdropFilter: 'blur(8px)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.2)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.1)')}
          >
            Start the party
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={heroBg}>
        {/* Floating reactions */}
        <div className="absolute inset-0 pointer-events-none">
          {reactions.map((r, i) => (
            <span
              key={i}
              className="cys-float absolute text-3xl"
              style={{
                left: `${8 + i * 20}%`,
                bottom: '12%',
                animationDelay: `${i * 1.3}s`,
                filter: 'drop-shadow(0 6px 12px rgba(0,0,0,.3))',
              }}
            >
              {r}
            </span>
          ))}
        </div>

        <div className="relative mx-auto text-center pt-36 pb-28" style={{ width: '92vw', maxWidth: 1120 }}>
          {userEmail && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10 px-4 py-3 rounded-2xl max-w-xl mx-auto" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.2)' }}>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,.72)' }}>
                Signed in as <span className="font-semibold text-white">{userEmail}</span>
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => navigate(accountDest)} className="px-4 py-2 rounded-full text-white text-sm font-semibold transition" style={{ background: C.punch }}>
                  My Dashboard
                </button>
                <button onClick={handleSignOut} disabled={signingOut} className="px-4 py-2 rounded-full text-sm font-medium text-white transition disabled:opacity-50" style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.35)' }}>
                  Sign out
                </button>
              </div>
            </div>
          )}

          <div className="inline-block px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.18em] mb-8" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.2)', color: C.sun }}>
            The Singing League presents · Game Night
          </div>

          <h1 className="font-black tracking-tight text-white" style={{ fontSize: 'clamp(3rem, 9vw, 6.5rem)', lineHeight: 1.04 }}>
            Can you sing?
            <span className="block" style={{ color: C.sun }}>Beat my score.</span>
          </h1>

          <p className="mx-auto mt-7 max-w-2xl text-lg sm:text-xl leading-relaxed" style={{ color: 'rgba(255,255,255,.72)' }}>
            Turn any room into the main stage. A real AI vocal judge scores every performance — honest, not the
            grandma-says-you're-great kind — then you dare the whole room to beat it. This isn't background noise.
            It's the whole night.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={goSignup}
              className="cys-pulse px-8 py-4 rounded-full text-white font-bold text-lg transition hover:-translate-y-0.5"
              style={{ background: C.punch }}
            >
              Start the party →
            </button>
            <button
              onClick={() => scrollTo('how-it-works')}
              className="px-8 py-4 rounded-full text-white font-semibold text-lg transition"
              style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.35)', backdropFilter: 'blur(8px)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.16)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.08)')}
            >
              See how it works
            </button>
          </div>

          <div className="mt-10 inline-flex items-center gap-3 px-6 py-3 rounded-full" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.2)' }}>
            <span>🏆</span>
            <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,.72)' }}>Tonight's score to beat:</span>
            <span className="text-xl font-black tabular-nums" style={{ color: C.sun }}>{SCORES[scoreIdx]}</span>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm" style={{ color: 'rgba(255,255,255,.55)' }}>
            <span>✦ No guest app</span>
            <span>✦ TV-ready</span>
            <span>✦ AI scoring</span>
          </div>
        </div>
      </section>

      {/* Vibe marquee */}
      <div className="overflow-hidden py-4" style={{ background: C.ink2 }}>
        <div className="cys-marquee flex whitespace-nowrap" style={{ width: 'max-content' }}>
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center" aria-hidden={dup === 1}>
              {['🎤 Can you sing?', '🏆 Beat my score', '🎂 Birthday parties', '🛋️ Squad nights', '👑 Crown a champion', '📱 Clip-ready moments', '👵 Grandma drops a 98'].map((item, i) => (
                <span key={i} className="px-8 text-base font-semibold" style={{ color: i % 7 === 0 ? C.sun : 'rgba(255,255,255,.5)' }}>
                  {item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Experience */}
      <section className="py-24" style={{ background: C.cloud }}>
        <div className="mx-auto" style={{ width: '92vw', maxWidth: 1120 }}>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-sm font-bold uppercase tracking-[0.18em] mb-4" style={{ color: C.grapeDeep }}>It's not karaoke. It's an event.</div>
            <h2 className="font-black tracking-tight" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.1, color: C.ink }}>
              Everyone's the main character for three minutes
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: '#5a4f70' }}>
              Most karaoke apps hand you lyrics and walk away. We built the part that actually makes a party — the
              score, the stakes, the screaming when someone nails the chorus and throws down a number to beat.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {experienceCards.map((card) => (
              <div key={card.title} className="cys-card rounded-3xl p-7 bg-white" style={{ border: `1px solid ${C.cloud2}`, boxShadow: '0 18px 50px -12px rgba(26,16,51,.12)' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-5" style={{ background: card.iconBg }}>
                  {card.icon}
                </div>
                <h3 className="text-xl font-extrabold mb-2" style={{ color: C.ink }}>{card.title}</h3>
                <p className="leading-relaxed" style={{ color: '#5a4f70' }}>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24" style={{ background: C.cloud2 }}>
        <div className="mx-auto" style={{ width: '92vw', maxWidth: 1120 }}>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-sm font-bold uppercase tracking-[0.18em] mb-4" style={{ color: C.grapeDeep }}>How it works</div>
            <h2 className="font-black tracking-tight" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.1, color: C.ink }}>
              Party started in three taps
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: '#5a4f70' }}>
              No setup degree required. If you can play a video, you can host this.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step) => (
              <div key={step.n} className="cys-card rounded-3xl p-8 bg-white" style={{ boxShadow: '0 18px 50px -12px rgba(26,16,51,.12)' }}>
                <div className="font-black mb-3" style={{ fontSize: '3.5rem', lineHeight: 1, color: C.grape }}>{step.n}</div>
                <h3 className="text-xl font-extrabold mb-2" style={{ color: C.ink }}>{step.title}</h3>
                <p className="leading-relaxed" style={{ color: '#5a4f70' }}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Generations */}
      <section className="py-24" style={{ background: C.cloud }}>
        <div className="mx-auto" style={{ width: '92vw', maxWidth: 1120 }}>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-sm font-bold uppercase tracking-[0.18em] mb-4" style={{ color: C.grapeDeep }}>For everyone</div>
            <h2 className="font-black tracking-tight" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.1, color: C.ink }}>
              Built for every party, every generation under one roof
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: '#5a4f70' }}>
              Eight-year-olds, teenagers, mom, dad, and grandma all want a turn — and somehow grandma keeps winning.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {generations.map((g) => (
              <div key={g.title} className="cys-card rounded-3xl p-6 flex flex-col justify-between min-h-[220px] text-white" style={{ background: g.gradient, boxShadow: '0 18px 50px -12px rgba(26,16,51,.35)' }}>
                <div className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,.8)' }}>{g.tag}</div>
                <div className="text-2xl font-black leading-tight">{g.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator / clips */}
      <section className="py-24" style={{ background: C.ink }}>
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center" style={{ width: '92vw', maxWidth: 1120 }}>
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.18em] mb-4" style={{ color: C.sun }}>Every singer's a star</div>
            <h2 className="font-black tracking-tight text-white" style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', lineHeight: 1.1 }}>
              Your living room is content now
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,.72)' }}>
              Every performance is captured automatically with the score baked right onto the video — ready to post
              before the next singer even grabs the mic.
            </p>
            <ul className="mt-8 space-y-4">
              {checklist.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0" style={{ background: 'rgba(34,194,138,.2)', color: C.mint }}>✓</span>
                  <span className="text-white">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Phone mockup */}
          <div className="flex justify-center">
            <div className="relative rounded-[2.5rem] p-3" style={{ background: '#0e0820', width: 280, boxShadow: '0 30px 70px -18px rgba(0,0,0,.6)' }}>
              <div className="relative rounded-[2rem] overflow-hidden flex flex-col items-center justify-center text-center px-6" style={{ background: 'linear-gradient(160deg, #2c1d57, #1a1033)', height: 540 }}>
                <div className="absolute top-5 left-5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white flex items-center gap-1.5" style={{ background: C.punch }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white inline-block" /> Live
                </div>
                <span className="absolute top-16 right-7 text-2xl cys-float" style={{ animationDelay: '0.5s' }}>🔥</span>
                <span className="absolute top-32 left-7 text-2xl cys-float" style={{ animationDelay: '1.8s' }}>👏</span>
                <div className="text-xs uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,.55)' }}>Your score</div>
                <div className="font-black tabular-nums my-2" style={{ fontSize: '5.5rem', lineHeight: 1, color: C.sun }}>{SCORES[scoreIdx]}</div>
                <div className="text-sm font-semibold text-white">Beat my score 🎤</div>
                <button className="mt-8 w-full py-3 rounded-full text-white font-bold" style={{ background: C.punch }}>
                  Take the mic →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20" style={{ background: C.cloud2 }}>
        <div className="mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10 text-center" style={{ width: '92vw', maxWidth: 1120 }}>
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-black" style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', lineHeight: 1, color: C.grapeDeep }}>{s.num}</div>
              <div className="mt-3 text-base" style={{ color: '#5a4f70' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24" style={{ background: C.cloud }}>
        <div className="mx-auto" style={{ width: '92vw', maxWidth: 1120 }}>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-sm font-bold uppercase tracking-[0.18em] mb-4" style={{ color: C.grapeDeep }}>Party passes</div>
            <h2 className="font-black tracking-tight" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.1, color: C.ink }}>
              Pick your party pass
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: '#5a4f70' }}>
              From a single night to a full month — grab the pass that fits your party.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier) => {
              const featured = !!tier.tag;
              return (
                <div
                  key={tier.name}
                  className="cys-card relative rounded-3xl p-7 bg-white flex flex-col"
                  style={{
                    border: featured ? `2px solid ${C.punch}` : `1px solid ${C.cloud2}`,
                    boxShadow: featured ? '0 22px 55px -12px rgba(255,90,60,.35)' : '0 18px 50px -12px rgba(26,16,51,.12)',
                  }}
                >
                  {tier.tag && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap" style={{ background: C.punch }}>
                      {tier.tag}
                    </div>
                  )}
                  <h3 className="text-lg font-extrabold" style={{ color: C.ink }}>{tier.name}</h3>
                  <div className="font-black my-3" style={{ fontSize: '2.5rem', lineHeight: 1, color: C.ink }}>{tier.price}</div>
                  <ul className="space-y-2.5 flex-1 mt-2">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm" style={{ color: '#3d3357' }}>
                        <span style={{ color: C.mint }}>✓</span> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={goSignup}
                    className="mt-6 w-full py-3 rounded-full font-bold text-white transition hover:-translate-y-0.5"
                    style={{ background: featured ? C.punch : C.ink }}
                  >
                    Get Started
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28" style={{ background: 'radial-gradient(800px 400px at 50% 0%, rgba(255,90,60,.18), transparent 60%), linear-gradient(160deg, #2a1c54, #1A1033)' }}>
        <div className="mx-auto text-center" style={{ width: '92vw', maxWidth: 760 }}>
          <div className="text-sm font-bold uppercase tracking-[0.18em] mb-4" style={{ color: C.sun }}>One mic, one champion</div>
          <h2 className="font-black tracking-tight text-white" style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)', lineHeight: 1.08 }}>
            Can you sing? Beat my score.
          </h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,.72)' }}>
            Someone's about to have the best party of the year. Might as well be you — and might as well be tonight.
          </p>
          <button onClick={goSignup} className="cys-pulse mt-9 px-10 py-4 rounded-full text-white font-bold text-lg transition hover:-translate-y-0.5" style={{ background: C.punch }}>
            Start the party →
          </button>
          <p className="mt-6 text-sm" style={{ color: 'rgba(255,255,255,.5)' }}>
            Free to start • No guest app • Works on the TV you already have
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 text-center" style={{ background: C.ink }}>
        <div className="flex items-center justify-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-base" style={{ background: logoGradient }}>🎤</div>
          <span className="font-extrabold text-white">Game Night</span>
        </div>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,.5)' }}>
          © The Singing League. Bring the house down, responsibly.
        </p>
      </footer>
    </div>
  );
};

export default CanYouSingLanding;
